import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Subject, debounceTime, filter, map, take, takeUntil, timer } from 'rxjs';
import { App_FormSketchField, App_PlanModel } from 'src/app/utils/interfaces';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-c-modal-plan',
  templateUrl: './c-modal-plan.component.html',
  styleUrls: ['./c-modal-plan.component.scss'],
})
export class CModalPlanComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('BoxContainer', { static: true }) boxContainer!: ElementRef;
  @ViewChild('ImageReferenceRatio', { static: true }) imageReferenceRatio!: ElementRef;
  @Input() title: string | undefined;
  @Input() imageDataBase64: string | undefined;
  @Input() initialDrawing: string | undefined;
  @Input() initialMarkers: App_PlanModel[] | undefined;
  @Input() canDraw: boolean = false;
  @Input() canSetMarkers: boolean = false;
  @Input() limitNbMarqueur: number = 100;
  @Input() labelsMarqueurs: [string, string][] | undefined;
  @Input() field!: App_FormSketchField;
  @HostListener('window:resize', ['$event']) onResize(event: any) {
    this.triggerReevaluateContainerDimensions$.next(true);
  }

  marqueurSize = environment.marqueurPlanSize;

  marqueurs: { x: number | string, y: number | string, label: string, description: string }[] = [];
  dessin: string | undefined;

  clearPadRegisseurSubject = new Subject();

  imgMaxHeight: string | undefined;
  cadreMaxHeight: string | undefined;

  imgDefinedWidth: number | undefined;
  imgDefinedHeight: number | undefined;

  triggerReevaluateContainerDimensions$ = new Subject();

  destroyComponent$ = new Subject();

  // options marqueurs

  addMarqueurTrigger$: Subject<string> = new Subject();
  deleteMarqueursTrigger$: Subject<any> = new Subject();
  resizedWindowTrigger$ = new Subject();

  // options dessin

  overwritePadDessin$: Subject<string | null> = new Subject();

  clearPadGeometric$ = new Subject();

  mode: 'marqueurs' | 'dessin' | undefined;

  dessinMode: 'rectangle' | 'ellipse' | 'line' | 'drawing' = 'drawing'

  dessinHistory: string[] = [];

  constructor(
    private modalController: ModalController,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (!this.canSetMarkers && !this.canDraw) {
      this.mode = undefined;
    } else {
      if (this.canSetMarkers) {
        this.mode = 'marqueurs';
      } else {
        this.mode = 'dessin';
      }
    }

    this.marqueurs = this.initialMarkers
    ? this.initialMarkers.map(e => ({
      x: e.x,
      y: e.y,
      label: e.label,
      description: e.description
    }))
    : [];
    // '' (pas undefined) pour que le canvas + image de fond s'affichent aussi en mode marqueurs seuls
    this.dessin = this.initialDrawing ?? '';
    this.dessinHistory = [this.dessin || '', ...this.dessinHistory].slice(0,10);

    this.triggerReevaluateContainerDimensions$.pipe(
      debounceTime(200),
      takeUntil(this.destroyComponent$),
    ).subscribe(_ => this.reevaluateContainerDimensions())
  }

  ngAfterViewInit() {
    if (this.imageDataBase64) {
      this.resolveBackgroundImageDimensions(this.imageDataBase64);
    } else {
      requestAnimationFrame(() => {
        this.defineImgDimensions({width: 300, height: 300})
        this.triggerReevaluateContainerDimensions$.next(true);
      })}
  }

  /**
   * Mesure via Image() (fiable pour URL distante / data-url).
   * Fallback : polling DOM de l'img de référence, puis 300×300.
   */
  private resolveBackgroundImageDimensions(src: string): void {
    const apply = (width: number, height: number) => {
      this.defineImgDimensions({ width, height });
      requestAnimationFrame(() => {
        this.triggerReevaluateContainerDimensions$.next(true);
      });
    };

    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      if (width > 0 && height > 0) {
        apply(width, height);
      } else {
        this.pollReferenceImageDimensions(apply);
      }
    };
    img.onerror = () => this.pollReferenceImageDimensions(apply);
    img.src = src;
  }

  private pollReferenceImageDimensions(apply: (width: number, height: number) => void): void {
    timer(0, 100).pipe(
      map(_ => this.imageReferenceRatio),
      filter(_ => _ !== undefined),
      map(_ => this.getDimensions(_)),
      filter(({width, height}) => typeof width === 'number' && width !== 0 && typeof height === 'number' && height !== 0),
      take(1),
      takeUntil(this.destroyComponent$),
    ).subscribe({
      next: ({ width, height }) => apply(width, height),
    });

    // Si l'image ne charge jamais (CORS / 404), débloquer l'UI
    timer(3000).pipe(take(1), takeUntil(this.destroyComponent$)).subscribe(() => {
      if (!this.imgMaxHeight) {
        apply(300, 300);
      }
    });
  }

  ngOnDestroy() {
    this.destroyComponent$.next(true);
  }


  closeModal() {
    this.modalController.dismiss(null);
  }

  validate() {
    this.modalController.dismiss({
      marqueurs: this.marqueurs,
      dessin: this.dessin,
    });
  }

  defineImgDimensions(values: {width: number, height: number}) {
    this.imgDefinedWidth = values.width;
    this.imgDefinedHeight = values.height;
  }

  getDimensions(element: ElementRef) {
    const width = element.nativeElement.offsetWidth;
    const height = element.nativeElement.offsetHeight;
    return {width, height};
  }

  getDimensionsBoxContainer(element: ElementRef) {
    const width = element.nativeElement.offsetWidth;
    const parent = element.nativeElement.parentElement;
    const parentHeight = parent.offsetHeight;
    const offsetTop = element.nativeElement.offsetTop;
    const height = parentHeight - offsetTop;
    return {width, height};
  }

  reevaluateContainerDimensions() {
    timer(0, 100).pipe(
      map(_ => this.boxContainer),
      filter(_ => _ !== undefined),
      map(_ => this.getDimensionsBoxContainer(_)),
      filter(({width, height}) => typeof width === 'number' && width !== 0 && typeof height === 'number' && height !== 0),
      take(1)
    ).subscribe(({width, height}) => {
      if (this.imgDefinedWidth && this.imgDefinedHeight) {
        const ratioImg = this.imgDefinedWidth / this.imgDefinedHeight;
        const ratioContainer = width / height;
        if (ratioContainer > ratioImg) {
          this.imgMaxHeight = `${Math.floor(height) - (this.marqueurSize + 4)}px`;
        } else {
          this.imgMaxHeight = `${Math.floor(width / ratioImg) - (this.marqueurSize + 4)}px`;
        }
        this.cadreMaxHeight = this.imgMaxHeight;
      }
      this.changeDetectorRef.detectChanges();
      requestAnimationFrame(() => {
        this.resizedWindowTrigger$.next(true);
      })
    })
  }

  addMarqueur() {
    if (this.marqueurs.length < this.limitNbMarqueur) {
      const labelsMarqueurs = this.labelsMarqueurs;
      const nextLabelToAdd = (searchArray: { x: number | string; y: number | string; label: string }[]): string | undefined => {
        // 1) Si des libellés prédéfinis existent, proposer d'abord un prédéfini pas encore utilisé
        if (labelsMarqueurs && labelsMarqueurs.length > 0) {
          for (const entry of labelsMarqueurs) {
            const label = entry[0];
            if (!searchArray.some((e) => e.label === label)) {
              return label;
            }
          }
        }
        // 2) Ensuite, proposer des libellés numériques (1, 2, 3, ...) jusqu'à limitNbMarqueur
        for (let index = 1; index <= this.limitNbMarqueur; index++) {
          const numLabel = index.toString();
          if (!searchArray.some((e) => e.label === numLabel)) {
            return numLabel;
          }
        }
        return undefined;
      };
      const nextLabel = nextLabelToAdd(this.marqueurs);
      if (typeof nextLabel === 'string') {
        this.addMarqueurTrigger$.next(nextLabel);
      }
    }
  }

  removeMarqueurs() {
    this.deleteMarqueursTrigger$.next(true);
  }

  reevaluateFinalDataMarqueurs($event: any) {
    this.marqueurs = $event;
  }

  reevaluateFinalDataDessin($event: {data: string}) {
    this.dessin = $event.data;
    if (this.dessin !== this.dessinHistory[0]) {
      this.dessinHistory = [this.dessin || '', ...this.dessinHistory].slice(0,10);
    }
  }

  clearDessin() {
    this.overwritePadDessin$.next(null);
  }

  modeMarqueur() {
    // if (this.mode !== `marqueurs`) {
      this.mode = `marqueurs`;
      this.dessinMode = 'drawing';
    // } else {
    //   this.modeVision();
    // }
  }

  modeDessin() {
    // if (this.mode !== `dessin`) {
      this.mode = `dessin`;
      this.dessinMode = 'drawing';
    // } else {
    //   this.modeVision();
    // }
  }

  modeVision() {
    this.mode = undefined;
    this.dessinMode = 'drawing';
  }

  onChangeDrawingmodeClick(dessinMode: 'rectangle' | 'ellipse' | 'line' | 'drawing') {
    this.dessinMode = dessinMode;
    this.clearPadGeometric$.next(true);
  }

  onBackToPreviousValueButtonClick() {
    if (this.dessinHistory.length > 1) {
      const [droppedData, newData, ...rest ] = this.dessinHistory;
      this.dessinHistory = [newData, ...rest].slice(0,10);
      if (typeof newData === 'string') {
        this.overwritePadDessin$.next(newData);
      }
    }
  }

  editMarqueurInfo($event: { label: string, description: string, index: number }) {
    const marqueurs = this.marqueurs;
    if (Array.isArray(marqueurs)) {
      const found = marqueurs[$event.index];
      if (found) {
        const newMarqueursValue = [
          ...marqueurs.slice(0, $event.index),
          {
            ...marqueurs[$event.index],
            label: $event.label,
            description: $event.description,
          },
          ...marqueurs.slice($event.index + 1)
        ];
        this.marqueurs = newMarqueursValue;
      }
    }
  }

}
